import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';

const CheckIcon = ({ size = 10, style = {} }) => (
  <svg width={size} height={size} viewBox="0 0 10 8" fill="none" style={{ display:'inline-block', verticalAlign:'middle', flexShrink:0, ...style }}>
    <path d="M1 4L3.8 7L9 1" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const API_BASE = process.env.REACT_APP_API_URL || 'https://backend-production-9457.up.railway.app';

export default function Projects() {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ name: '', description: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [modalError, setModalError] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadProjects(); }, []);

  useEffect(() => {
    if (!showModal) return;
    const onKey = (e) => { if (e.key === 'Escape') setShowModal(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [showModal]);

  async function loadProjects() {
    setLoading(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/v1/projects`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setProjects(await res.json());
    } catch { setError('Could not load projects.'); }
    finally { setLoading(false); }
  }

  async function createProject(e) {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    setModalError(null);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/v1/projects`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: form.name.trim(), description: form.description.trim() || null }),
      });
      if (res.ok) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: 'project_created' });
        setForm({ name: '', description: '' });
        setShowModal(false);
        await loadProjects();
      } else {
        const data = await res.json().catch(() => ({}));
        setModalError(data.detail || `Error ${res.status} — please try again`);
      }
    } catch {
      setModalError('Network error — please try again');
    } finally { setSaving(false); }
  }

  const copyId = useCallback((id) => {
    navigator.clipboard.writeText(id);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  }, []);

  async function deleteProject(id) {
    if (!window.confirm('Delete this project? Sessions will be unassigned.')) return;
    const token = await getToken();
    await fetch(`${API_BASE}/v1/projects/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    setProjects(p => p.filter(x => x.id !== id));
  }

  if (loading) return <div style={s.center}>Loading projects...</div>;

  return (
    <div>
      {/* Header */}
      <div style={s.header}>
        <div>
          <h1 style={s.h1}>Projects</h1>
          <p style={s.subtitle}>Organise your research sessions into projects</p>
        </div>
        <button style={s.newBtn} onClick={() => { setShowModal(true); setModalError(null); }}>+ New project</button>
      </div>

      {error && <p style={s.errorMsg}>{error}</p>}

      {/* Project grid */}
      {projects.length === 0 ? (
        <div style={s.empty}>
          <p style={s.emptyTitle}>No projects yet</p>
          <p style={s.emptyDesc}>Create a project to group related research sessions together.</p>
          <button style={s.newBtn} onClick={() => setShowModal(true)}>Create your first project</button>
        </div>
      ) : (
        <div style={s.grid}>
          {projects.map(p => (
            <div key={p.id} className="scq-card" style={s.card} onClick={() => navigate(`/projects/${p.id}`)}>
              <div style={s.cardTop}>
                <div style={s.projectIcon}>✦</div>
                <button style={s.deleteBtn} onClick={e => { e.stopPropagation(); deleteProject(p.id); }} title="Delete project">✕</button>
              </div>
              <h2 style={s.projectName}>{p.name}</h2>
              {p.description && <p style={s.projectDesc}>{p.description.length > 100 ? p.description.slice(0, 100) + '…' : p.description}</p>}
              <button style={s.idChip} onClick={e => { e.stopPropagation(); copyId(p.id); }} title="Click to copy project ID">
                {copiedId === p.id ? <><CheckIcon size={10} style={{marginRight:4}} />Copied</> : `ID: ${p.id.slice(0, 8)}…`}
              </button>
              <div style={s.cardFooter}>
                <span style={s.sessionCount}>{p.session_count} session{p.session_count !== 1 ? 's' : ''}</span>
                <span style={s.cardDate}>{p.created_at ? new Date(p.created_at).toLocaleDateString() : ''}</span>
              </div>
              <button
                style={s.chatBtn}
                onClick={e => { e.stopPropagation(); navigate('/chat', { state: { projectId: p.id, projectName: p.name } }); }}
              >
                ✦ Research chat
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Create project modal */}
      {showModal && createPortal(
        <div style={s.overlay} onClick={() => setShowModal(false)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <h2 style={s.modalTitle}>New project</h2>
            {modalError && <p style={s.modalError}>{modalError}</p>}
            <form onSubmit={createProject}>
              <label style={s.label}>Name *</label>
              <input
                style={s.input}
                placeholder="e.g. Q1 User Interviews"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                autoFocus
              />
              <label style={s.label}>Description <span style={{color:'#94A3B8'}}>(optional)</span></label>
              <textarea
                style={{...s.input, height:80, resize:'vertical'}}
                placeholder="What is this project about?"
                value={form.description}
                maxLength={150}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              />
              <div style={s.charCount}>{form.description.length}/150</div>
              <div style={s.modalActions}>
                <button type="button" style={s.cancelBtn} onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" style={{...s.saveBtn, opacity: saving || !form.name.trim() ? 0.6 : 1}} disabled={saving || !form.name.trim()}>
                  {saving ? 'Creating…' : 'Create project'}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

const s = {
  center:       { color: 'var(--color-muted)', textAlign: 'center', padding: '80px 0', fontSize: 14 },
  header:       { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 32 },
  h1:           { fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--color-text)', margin: 0 },
  subtitle:     { color: 'var(--color-muted)', marginTop: 6, fontSize: 14 },
  newBtn:       { padding: '10px 18px', background: 'var(--color-magma)', color: '#fff', border: 'none', borderRadius: 'var(--radius-lg)', fontSize: 13, fontFamily: 'var(--font-display)', fontWeight: 700, cursor: 'pointer' },
  errorMsg:     { color: 'var(--color-danger)', fontSize: 13, marginBottom: 16 },
  empty:        { textAlign: 'center', padding: '60px 24px', background: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-xl)' },
  emptyTitle:   { fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700, color: 'var(--color-text)', margin: '0 0 8px' },
  emptyDesc:    { color: 'var(--color-muted)', fontSize: 14, marginBottom: 24 },
  grid:         { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 },
  card:         { background: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-xl)', padding: 24, display: 'flex', flexDirection: 'column', gap: 8, cursor: 'pointer', boxShadow: 'var(--shadow-card)' },
  cardTop:      { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 },
  projectIcon:  { fontSize: 18, color: 'var(--color-accent)', opacity: 0.7 },
  deleteBtn:    { background: 'none', border: 'none', color: 'var(--color-muted2)', fontSize: 14, cursor: 'pointer', padding: '2px 6px', borderRadius: 'var(--radius-sm)', lineHeight: 1 },
  projectName:  { fontFamily: 'var(--font-display)', fontSize: 17, fontWeight: 700, letterSpacing: '-0.01em', color: 'var(--color-text)', margin: 0 },
  projectDesc:  { fontSize: 13, color: 'var(--color-muted)', margin: 0, lineHeight: 1.5 },
  cardFooter:   { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8, paddingTop: 12, borderTop: '1px solid var(--color-border)' },
  idChip:       { display: 'inline-block', fontSize: 11, fontFamily: 'monospace', color: 'var(--color-muted2)', background: 'var(--color-chip-bg)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', padding: '3px 8px', cursor: 'pointer', textAlign: 'left' },
  sessionCount: { fontSize: 12, color: 'var(--color-accent)', fontWeight: 600 },
  cardDate:     { fontSize: 12, color: 'var(--color-muted2)' },
  chatBtn:      { width: '100%', marginTop: 4, padding: '9px 0', background: 'var(--color-accent-dim)', color: 'var(--color-accent)', border: '1px solid var(--color-accent-border)', borderRadius: 'var(--radius-lg)', fontSize: 12, fontFamily: 'var(--font-display)', fontWeight: 700, cursor: 'pointer', letterSpacing: '-0.01em' },
  overlay:      { position: 'fixed', inset: 0, background: 'rgba(3,10,60,0.7)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: 24 },
  modal:        { background: 'rgba(255,255,255,0.96)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', border: '1px solid rgba(226,232,240,0.8)', borderRadius: 'var(--radius-xl)', boxShadow: '0 24px 64px rgba(15,23,42,0.18), 0 4px 16px rgba(15,23,42,0.08), inset 0 1px 0 rgba(255,255,255,1)', padding: '28px 28px 24px', width: '100%', maxWidth: 440 },
  modalTitle:   { fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700, color: '#0F172A', margin: '0 0 20px' },
  label:        { display: 'block', fontSize: 12, fontWeight: 600, color: '#64748B', marginBottom: 6, marginTop: 16, letterSpacing: '0.04em', textTransform: 'uppercase' },
  input:        { width: '100%', boxSizing: 'border-box', fontFamily: 'var(--font-sans)', fontSize: 14, padding: '11px 14px', borderRadius: 'var(--radius-lg)', border: '1px solid #E2E8F0', background: '#F8FAFC', color: '#0F172A', outline: 'none' },
  modalActions: { display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 24 },
  modalError:   { color: '#DC2626', fontSize: 13, marginBottom: 8, padding: '8px 12px', background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.2)', borderRadius: 'var(--radius-md)' },
  charCount:    { fontSize: 11, color: '#94A3B8', textAlign: 'right', marginTop: 4 },
  cancelBtn:    { padding: '10px 16px', background: 'transparent', color: '#64748B', border: '1px solid #E2E8F0', borderRadius: 'var(--radius-lg)', fontSize: 13, fontFamily: 'var(--font-display)', fontWeight: 600, cursor: 'pointer' },
  saveBtn:      { padding: '10px 20px', background: 'var(--color-magma)', color: '#fff', border: 'none', borderRadius: 'var(--radius-lg)', fontSize: 13, fontFamily: 'var(--font-display)', fontWeight: 700, cursor: 'pointer' },
};
